"use client";

import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { FaChevronDown } from "react-icons/fa";
import "./home.css"; // The homepage-specific CSS
import "../components/IndustriesWeServe.css";
import { Image } from "react-bootstrap";
import dynamic from "next/dynamic";
import BannerSection from "../components/BannerSection";
const ProcessSection = dynamic(
  () => import("./ProcessSection"),
  {
    ssr: true,
  }
);
const BrandsSlider = dynamic(
  () => import("../components/BrandsSlider"),
  {
    ssr: true,
  }
);
const AiChatBotDashboardSection = dynamic(
  () => import("../components/AiChatBotDashboardSection"),
  {
    ssr: true,
  }
);
const CaseStudiesScroll = dynamic(
  () => import("../components/CaseStudiesScroll"),
  {
    ssr: true,
  }
);
const TeamsWorkSection = dynamic(
  () => import("../components/TeamsWorkSection"),
  {
    ssr: true,
  }
);
const TechnologiesSection = dynamic(
  () => import("@/components/TechnologiesSection"),
  {
    ssr: true,
  }
);
const HomeTechnologies = dynamic(
  () => import("../components/HomeTechnologies"),
  {
    ssr: true,
  }
);
const BlogsMarquee = dynamic(
  () => import("../components/BlogsMarquee"),
  {
    ssr: true,
  }
);
const TestimonialsSection = dynamic(
  () => import("../components/TestimonialsSection"),
  {
    ssr: true,
  }
);
const IndustriesWeServe = dynamic(
  () => import("../components/IndustriesWeServe"),
  {
    ssr: true,
  }
);
const WhyChooseSection = dynamic(
  () => import("../components/WhyChooseSection"),
  {
    ssr: true,
  }
);

// --- DATA ARRAYS ---
// All image paths are now strings pointing to the /public folder
const words = [
  "Customer Support",
  "Passion",
  "Positive Experience",
  "Trustworthiness",
  "Customer Focus",
  "Long-term Relation",
  "Innovation",
  "Collaboration",
  "Personalization",
  "Reliability",
  "Quality",
];


export default function HomePageClient({ initialHomeData }) {
  const [homeData, setHomeData] = useState(initialHomeData || null);

  useEffect(() => {
    // If we already have data from SSR, skip client-side fetch to save network request
    if (initialHomeData) return;

    const fetchHomeData = async () => {
      try {
        const res = await fetch("/api/home/all?isActive=true");
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        const contentType = res.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          throw new TypeError("Response was not JSON");
        }
        const json = await res.json();
        if (json.success && json.data?.homePages?.length > 0) {
          setHomeData(json.data.homePages[0]);
        }
      } catch (err) {
        console.warn("Failed to fetch homepage dynamic data (using static fallback):", err.message || err);
      }
    };
    fetchHomeData();
  }, []);

  const getImageUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    if (url.startsWith("data:") || url.startsWith("blob:")) return url;
    
    let finalUrl = url;
    const apiUrl = (process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_API_BASE_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '') || "";
    
    if (url.includes("/uploads/")) {
      const cleanPath = url.substring(url.indexOf("/uploads/"));
      finalUrl = `${apiUrl}${cleanPath}`;
    } else if (url.startsWith("uploads/") || url.startsWith("/uploads/")) {
      const cleanPath = url.startsWith("/") ? url : `/${url}`;
      finalUrl = `${apiUrl}${cleanPath}`;
    } else if (url.startsWith("http")) {
      finalUrl = url;
    }

    if (finalUrl.includes("res.cloudinary.com") && finalUrl.includes("/image/upload/")) {
      if (!finalUrl.includes("f_auto") && !finalUrl.includes("q_auto")) {
        finalUrl = finalUrl.replace("/image/upload/", "/image/upload/f_auto,q_auto/");
      }
    }
    return finalUrl;
  };



  const [show, setShow] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    contact: "",
    message: "",
  });
  const handleShow = () => setShow(true);
  const handleClose = () => setShow(false);
  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleSubmit = (e) => {
    e.preventDefault();
    handleClose();
  };
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [showVideo, setShowVideo] = useState(false);
  const playButtonRef = useRef(null);
  const aboutSectionRef = useRef(null);
  const openVideo = () => setShowVideo(true);
  const closeVideo = (e) => {
    e.stopPropagation();
    setShowVideo(false);
  };
  const circleRef = useRef(null);

  // Hide the scrollbar on the home page only (scrolling still works)
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.body.classList.add("hide-scrollbar");
    }
    return () => {
      if (typeof document !== "undefined") {
        document.body.classList.remove("hide-scrollbar");
      }
    };
  }, []);


  useEffect(() => {
    const animateWords = () => {
      const wordElements = document.querySelectorAll(".word");
      wordElements.forEach((word, index) => {
        setTimeout(() => {
          word.style.opacity = "1";
          word.style.transform = "translateY(0)";
        }, index * 300);
      });
    };
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateWords();
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );
    if (circleRef.current) observer.observe(circleRef.current);
  }, []);

  const handleSectionMouseMove = (e) => {
    if (!aboutSectionRef.current || !playButtonRef.current) return;
    const clientX = e.clientX;
    const clientY = e.clientY;
    window.requestAnimationFrame(() => {
      if (!aboutSectionRef.current || !playButtonRef.current) return;
      const rect = aboutSectionRef.current.getBoundingClientRect();
      const buttonWidth = playButtonRef.current.offsetWidth;
      const buttonHeight = playButtonRef.current.offsetHeight;
      setPosition({
        x: clientX - rect.left - buttonWidth / 2,
        y: clientY - rect.top - buttonHeight / 2,
      });
    });
  };

  const displayWords = (homeData?.whyChooseSection?.tags && homeData.whyChooseSection.tags.length > 0)
    ? homeData.whyChooseSection.tags.slice(0, 11)
    : words;



  const displayLocations = (homeData?.officeLocationsSection?.locations && homeData.officeLocationsSection.locations.length > 0)
    ? homeData.officeLocationsSection.locations
    : null;

  return (
    <div
      style={{
        backgroundColor: "rgba(0,0,0,0)",
        minHeight: "100vh",
        overflowX: "clip",
      }}
    >
      <div
        id="large-header"
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          overflow: "hidden",
          zIndex: -2,
        }}
      ></div>
      {/* <div className="hero">
        <h1>
          Transforming
          <br /> Ideas into <br />
          <span ref={el} />
        </h1>
        <p>
          At CodeQlik, we blend design, sustainability & expertise to transform{" "}
          <br /> your vision with tech-driven precision. Elevate your projects
          with innovation.
        </p>
        <div className="hero-btn-row">
          <button className="cta-button" onClick={handleShow}>
            {`Let's Connect`} <span className="arrow">↗</span>
          </button>
        </div>
        {show && (
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ background: "rgba(0,0,0,0.35)", zIndex: 9999 }}
          >
            <div className="modal-dialog custom-modal-dialog">
              <div className="modal-content custom-modal-content">
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleClose}
                  aria-label="Close"
                  style={{
                    position: "absolute",
                    top: 18,
                    right: 18,
                    zIndex: 2,
                    background: "none",
                    border: "none",
                    fontSize: 22,
                    color: "#888",
                    cursor: "pointer",
                  }}
                >
                  &#10005;
                </button>
                <div className="modal-header">
                  <h5 className="modal-title" style={{ color: "#b2b2b2" }}>
                    Contact Us
                  </h5>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <label className="form-label">Name:</label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter your name"
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Email:</label>
                      <input
                        type="email"
                        className="form-control"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter your email"
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Contact Number:</label>
                      <input
                        type="tel"
                        className="form-control"
                        name="contact"
                        value={formData.contact}
                        onChange={handleChange}
                        placeholder="Enter your phone number"
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Your Message:</label>
                      <textarea
                        className="form-control"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows="3"
                        placeholder="Type your message here..."
                        required
                      ></textarea>
                    </div>
                    <button type="submit" className="custom-modal-submit">
                      Submit
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}
      </div> */}

      <BannerSection
        title={homeData?.banner?.title}
        title2={homeData?.banner?.title2}
        description={homeData?.banner?.description}
        tags={homeData?.banner?.tags}
      />

      <BrandsSlider />
      {/* <section>
        <div
          className="about-section"
          data-aos="fade-up"
          data-aos-delay="500"
          ref={aboutSectionRef}
          onMouseMove={handleSectionMouseMove}
          onClick={openVideo}
          style={{ position: "relative", cursor: "pointer" }}
        >
          <div className="background-gradient"></div>
          <div className="hexagon-container">
            <div className="hexagn">
              <Image
                src="/assets/img/team_img.png"
                alt="Team"
                className="hex-image"
              />
            </div>
            <div
              className="circleA"
              ref={playButtonRef}
              style={{
                left: `${position.x}px`,
                top: `${position.y}px`,
                position: "absolute",
                zIndex: 9999,
                pointerEvents: "none",
              }}
            >
              <i className="fa-solid fa-play"></i>
              <div className="circle-textA">
                <svg viewBox="0 0 200 200">
                  <defs>
                    <path
                      id="circlePathA"
                      d="M 100, 100 m -75, 0 a 75,75 0 1,1 150,0 a 75,75 0 1,1 -150,0"
                    />
                  </defs>
                  <text>
                    <textPath href="#circlePathA">
                      KNOW ABOUT US ★ CODEQLIK IT SOLUTION ★ PLAY THE VIDEO ★
                    </textPath>
                  </text>
                </svg>
              </div>
            </div>
          </div>
          <marquee direction="left" behavior="scroll" scrollamount="7">
            Innovating with AI to empower industries Innovating with AI to
            empower industries
          </marquee>
          {showVideo && (
            <div
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 10001,
                background: "rgba(0,0,0,0.95)",
                borderRadius: 16,
                padding: 16,
                boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeVideo}
                style={{
                  alignSelf: "flex-end",
                  marginBottom: 8,
                  background: "#1b1a1a",
                  border: "none",
                  borderRadius: 4,
                  padding: "4px 12px",
                  cursor: "pointer",
                  fontWeight: 700,
                  color: "white",
                }}
              >
                ✕
              </button>
              <div
                style={{ width: 660, maxWidth: "90vw", aspectRatio: "16/9" }}
              >
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/Eo0RzDoamxY?autoplay=1"
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          )}
        </div>
      </section>

      <div className="card-customA">
        <Link href="/reviews" className="text-decoration-none">
          <h5 className="fw-bold">IT SOLUTIONS COMPANY</h5>
          <hr />
          <div className="d-flex">
            <div className="d-flex justify-content-center my-3 profile-imagesA">
              <Image src="/assets/img/download2.jpeg" alt="Satisfied Client Profile" />
              <Image src="/assets/img/download3.jpeg" alt="Satisfied Client Profile" />
              <Image src="/assets/img/download4.jpeg" alt="Satisfied Client Profile" />
            </div>
            <div className="ms-4">
              <span className="starsA">★★★★★</span>
              <p className="mb-0 text-[13px]">200+ 5 Star Reviews</p>
            </div>
          </div>
        </Link>
      </div> */}

      {/* <div className="container-fluid py-5">
        <div className="why mt-5">
          <div
            className="why-header-home"
            data-aos="fade-up"
            data-aos-delay="0"
          >
            <span>Our Services</span>
            <h2>{homeData?.servicesSection?.title || "What We Offer"}</h2>
          </div>
          <div className="row justify-content-center">
            <div
              className="why-text1 col-12 col-lg-8"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <p className="right-textA">
                {homeData?.servicesSection?.description || "We provide a wide range of innovative and tailored solutions to meet your business needs. Our expertise ensures high-quality results that drive growth and success."}
              </p>
            </div>
          </div>
        </div>
        <div className="row mt-10">
          {homeData?.servicesSection?.services && homeData.servicesSection.services.length > 0 ? (
            homeData.servicesSection.services.map((service, idx) => (
              <div
                key={idx}
                className="col-lg-4 col-md-6 mb-4"
                data-aos="fade-up"
                data-aos-delay={((idx % 3) + 1) * 100}
              >
                <div className="service-card">
                  <div className="service-icon">
                    {renderIcon(service.icon || "Globe")}
                  </div>
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-title">{service.description}</p>
                </div>
              </div>
            ))
          ) : (
            <>
              <div
                className="col-lg-4 col-md-6 mb-4"
                data-aos="fade-up"
                data-aos-delay="100"
              >
                <div className="service-card">
                  <div className="service-icon">
                    <i className="fa-solid fa-globe"></i>
                  </div>
                  <h3 className="service-title">Web Application</h3>
                  <p className="service-title">
                    We develop custom, high-performance web applications that are
                    scalable, secure, and built to solve real business challenges.
                    Our full-stack solutions deliver fast, responsive, and intuitive
                    user experiences using the latest technologies. From internal
                    tools to customer-facing platforms, we create web apps that
                    drive efficiency, engagement, and long-term growth.
                  </p>
                </div>
              </div>
              <div
                className="col-lg-4 col-md-6 mb-4"
                data-aos="fade-up"
                data-aos-delay="200"
              >
                <div className="service-card">
                  <div className="service-icon">
                    {" "}
                    <i className="fa-solid fa-robot"></i>
                  </div>
                  <h3 className="service-title">Artificial Intelligence</h3>
                  <p className="service-title">
                    {`We deliver smart, AI-powered solutions that enhance
                    decision-making, automate processes, and drive innovation. From
                    machine learning models to intelligent chatbots and predictive
                    analytics, our AI services are designed to solve complex
                    problems with precision and scalability. Whether you're looking
                    to implement computer vision, natural language processing, or
                    recommendation systems, we tailor AI tools to meet your unique
                    business goals and unlock new growth opportunities.`}
                  </p>
                </div>
              </div>
              <div
                className="col-lg-4 col-md-6 mb-4"
                data-aos="fade-up"
                data-aos-delay="300"
              >
                <div className="service-card">
                  <div className="service-icon">
                    <i className="fa-brands fa-android"></i>
                  </div>
                  <h3 className="service-title">
                    Mobile Application Development Solution
                  </h3>
                  <p className="service-title">
                    We develop scalable, secure, and intuitive mobile apps for iOS,
                    Android, and cross-platform solutions. Whether you need a
                    feature-rich Android app, an engaging iOS application, or a
                    seamless multi-platform experience, we ensure top-tier
                    performance and user engagement.
                  </p>
                </div>
              </div>
              <div
                className="col-lg-4 col-md-6 mb-4"
                data-aos="fade-up"
                data-aos-delay="100"
              >
                <div className="service-card">
                  <div className="service-icon">
                    {" "}
                    <i className="fa-solid fa-gears"></i>
                  </div>
                  <h3 className="service-title">AI Workflow Automation</h3>
                  <p className="service-title">
                    {`We design and implement AI-driven workflow automation solutions
                    that streamline business processes, reduce manual effort, and
                    boost operational efficiency. By integrating machine learning,
                    smart decision-making, and real-time data processing, we help
                    automate repetitive tasks, approvals, and data flows across
                    systems. Whether it's sales, customer support, HR, or finance,
                    our automation tools are tailored to save time, reduce errors,
                    and scale with your business.`}
                  </p>
                </div>
              </div>
              <div
                className="col-lg-4 col-md-6 mb-4"
                data-aos="fade-up"
                data-aos-delay="200"
              >
                <div className="service-card">
                  <div className="service-icon">
                    {" "}
                    <i className="fa-solid fa-chart-line"></i>
                  </div>
                  <h3 className="service-title">Software Development</h3>
                  <p className="service-title">
                    {`We build powerful, scalable, and user-focused software solutions tailored to modern business needs. From custom web applications and enterprise systems to SaaS platforms and automation tools, our development process focuses on performance, security, and seamless user experience. By combining innovative technologies with clean architecture, we create software that improves productivity, simplifies operations, and supports long-term business growth. Whether you're a startup or an established enterprise, our solutions are designed to adapt, scale, and deliver measurable results in today’s fast-moving digital world.`}
                  </p>
                </div>
              </div>
              <div
                className="col-lg-4 col-md-6 mb-4"
                data-aos="fade-up"
                data-aos-delay="300"
              >
                <div className="service-card">
                  <div className="service-icon">
                    {" "}
                    <i className="fa-solid fa-vr-cardboard"></i>
                  </div>
                  <h3 className="service-title">AR/VR Development</h3>
                  <p className="service-title">
                    We create immersive Augmented Reality (AR) and Virtual Reality
                    (VR) experiences that engage users in entirely new ways. From
                    interactive 3D visualizations to virtual tours and training
                    simulations, our AR/VR solutions blend cutting-edge technology
                    with creative design to transform how businesses connect with
                    their audience. Whether it’s for marketing, education, or
                    entertainment, we deliver powerful, realistic, and scalable
                    AR/VR applications tailored to your goals.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div> */}

      <IndustriesWeServe data={homeData?.servicesSection} />

      <AiChatBotDashboardSection />

      {/* <BrandsSlider /> */}

      <WhyChooseSection data={homeData?.whyChooseSection} />

      <ProcessSection />

      {/* <TeamsWorkSection /> */}

      <CaseStudiesScroll
        title={homeData?.caseStudySection?.title}
        projects={homeData?.caseStudySection?.projects}
      />


      {/* <section className="case-studies-section text-white py-5">
        <div className="container-fluid case-studie-padding">
          <div
            className="text-center mb-5"
            data-aos="fade-up"
            data-aos-delay="0"
          >
            <span className="brands">works</span>
            <h2 className="mt-3 brand-trust">Case Studies</h2>
          </div>
          <div className="row g-4">
            <div className="col-md-6" data-aos="fade-up" data-aos-delay="0">
              <Link href="/projects/fot" className="text-decoration-none">
                <div className="card_imageA bg-dark case-studies">
                  <Image
                    src="/assets/img/FOTWW.png"
                    className="card-img-top"
                    alt="Showreel"
                  />
                </div>
                <div className="card-body work-text">
                  <span className="card-head">Case Study</span>
                  <h4 className="card-title">Food Ordering App and Web</h4>
                  <p className="card-text">
                    From Idea to Execution,Execution to Completion{" "}
                  </p>
                  <div className="d-flex justify-content-between">
                    <div className="work-percent">
                      <h6 className="mb-0">40%</h6>
                      <small>Increase in website traffic</small>
                    </div>
                    <div className="work-percent">
                      <h6 className="mb-0">70%</h6>
                      <small>Target keywords ranked</small>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
            <div
              className="col-md-6 case-studie"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <Link href="/projects/dakzy" className="text-decoration-none">
                <div className="card_imageA bg-dark case-studies">
                  <Image
                    src="/assets/img/projects/projects/dakzy/Dakzy cover.png"
                    className="card-img-top"
                    alt=""
                    width={500}
                    height={300}
                  />
                </div>
                <div className="card-body work-text">
                  <span className="card-head">Case Study</span>
                  <h4 className="card-title">
                    Local Store to E-Commerce Business
                  </h4>
                  <p className="card-text">
                    Want To Business Online Give Us Your Idea
                  </p>
                  <div className="d-flex justify-content-between">
                    <div className="work-percent">
                      <h6 className="mb-0">60%</h6>
                      <small>Increase in organic traffic</small>
                    </div>
                    <div className="work-percent">
                      <h6 className="mb-0">#1</h6>
                      <small>Rank on Google</small>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
            <div className="col-md-6 " data-aos="fade-up" data-aos-delay="100">
              <Link href="/projects/girlsslay" className="text-decoration-none">
                <div className="card_imageA bg-dark case-studies ">
                  <Image
                    src="/assets/img/projects/projects/girlsslay/girls-slay-cover.webp"
                    className="card-img-top"
                    alt=""
                    width={500}
                    height={300}
                  />
                </div>
                <div className="card-body work-text">
                  <span className="card-head">Case Study</span>
                  <h4 className="card-title">
                    How Girls Slay Grew Its Clientele{" "}
                  </h4>
                  <p className="card-text">
                    Boosting client bookings through targeted digital marketing
                    strategies
                  </p>
                  <div className="d-flex justify-content-between">
                    <div className="work-percent">
                      <h6 className="mb-0">30%</h6>
                      <small>New client bookings</small>
                    </div>
                    <div className="work-percent">
                      <h6 className="mb-0">50%</h6>
                      <small>Local search increase</small>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
            <div
              className="col-md-6 case-studie"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <Link href="/projects/myacebazar" className="text-decoration-none">
                <div className="card_imageA bg-dark case-studies">
                  <Image
                    src="/assets/img/projects/projects/myacebazar/yacebazar.webp"
                    className="card-img-top"
                    alt=""
                    width={500}
                    height={300}
                  />
                </div>
                <div className=" card-body work-text">
                  <span className="card-head">Case Study</span>
                  <h4 className="card-title">
                    One Stop Shopping solution With Easy Access
                  </h4>
                  <p className="card-text">
                    How Tech Haven skyrocketed its online sales by 40%
                  </p>
                  <div className="d-flex justify-content-between">
                    <div className="work-percent">
                      <h6 className="mb-0">60%</h6>
                      <small>Increase in SEO performance</small>
                    </div>
                    <div className="work-percent">
                      <h6 className="mb-0">80%</h6>
                      <small>Conversion rate</small>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>
          <div
            className="text-center arrow mt-5"
            data-aos="fade-up"
            data-aos-delay="0"
          >
            <Link href="/projects" className="cta-button">
              View All Projects <span className="arrow">↗</span>
            </Link>
          </div>
        </div>
      </section> */}
      <TechnologiesSection />

      <TestimonialsSection />

      {/* <HomeTechnologies
        title={homeData?.technologiesSection?.title}
        description={homeData?.technologiesSection?.description}
        categories={homeData?.technologiesSection?.categories}
      /> */}

      {/* <section className="why-us container-fluid">
        <div className="why mt-5">
          <div
            className="why-header-home"
            data-aos="fade-up"
            data-aos-delay="0"
          >
            <span>Why Us</span>
            <h2>Our Specialities</h2>
          </div>
          <div className="row">
            <div className="why-text1 " data-aos="fade-up" data-aos-delay="100">
              <p className="right-text">
                Our company not only delivers impressive results but also
                dazzles our clients with remarkable statistics. We pride
                ourselves on our ability to amaze customers with tangible
                outcomes and eye-catching figures.
              </p>
            </div>
          </div>
        </div>
        <div className="container-fluid">
          <div className="row why-text">
            <div
              className="col-lg-8 col-12 mt-3 image-padding"
              data-aos="fade-up"
              data-aos-delay="0"
            >
              <div className="image-overlay">
                <div className="text-overlay Specialities-first">
                  <h2>1000+</h2>
                  <p>Achieved Over 1,000 Successful Digital Transformations</p>
                  <div className="text-overlay1">
                    <p>
                      Our expertise in web development, mobile app solutions,
                      and digital marketing has helped businesses across
                      industries digitally transform and thrive. With over 1,000
                      successful projects, we empower brands to innovate, scale,
                      and stay ahead of the curve.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="col-lg-4 col-12 box-bg mt-3"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <div className="text-box">
                <h2>150%</h2>
                <p>Boost in User Engagement</p>
              </div>
            </div>
          </div>
          <div className="row">
            <div
              className="col-lg-6 col-12 mt-3 image-padding"
              data-aos="fade-up"
              data-aos-delay="0"
            >
              <div className="box_home">
                <div className="circle-container" ref={circleRef}>
                  {displayWords.map((word, index) => (
                    <div key={index} className={`word word-${index + 1}`}>
                      <Image
                        width="18"
                        height="18"
                        src="https://img.icons8.com/external-tanah-basah-glyph-tanah-basah/48/external-Atom-education-tanah-basah-glyph-tanah-basah.png"
                        alt=""
                      />
                      {word}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div
              className="col-lg-6 col-12 box2-home mt-3"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <div className="box-text">
                <h2>Enhanced Brand Visibility</h2>
                <p>80%</p>
              </div>
              <p className="box-textA">
                With SEO, social media marketing, and tailored advertising
                strategies, our clients have seen an 80% improvement in brand
                visibility, turning businesses into household names.
              </p>
            </div>
          </div>
        </div>
      </section> */}



      {/* <section>
        <div className="container py-5" data-aos="fade-up" data-aos-delay="0">
          <h2 className="section-title-home mb-3">Technologies We Support</h2>
          <p className="text-center">
            {`Our experts are proficient in a wide range of technologies, ensuring
            that you have the right skills for any project. Here's a snapshot of
            the technologies we support:`}
          </p>
          <div className="row g-4">
            <div className="col-md-6" data-aos="fade-up" data-aos-delay="100">
              <div className="tech-box">
                <h4 className="category-title">Backend</h4>
                <p className="tech-title">Java</p>
                <span className="tech-pill">SE 8/11</span>
                <span className="tech-pill">Spring</span>
                <span className="tech-pill">Spring Boot</span>
                <span className="tech-pill">Hibernate</span>
                <span className="tech-pill">Liquibase</span>
                <p className="tech-title">Python</p>
                <span className="tech-pill">Django</span>
                <span className="tech-pill">Flask</span>
                <span className="tech-pill">Pyramid</span>
                <span className="tech-pill">FastAPI</span>
                <span className="tech-pill">Asyncio</span>
                <p className="tech-title">C#</p>
                <span className="tech-pill">ASP.NET Core/EF</span>
                <span className="tech-pill">MediatR</span>
                <span className="tech-pill">AutoMapper</span>
                <span className="tech-pill">Serilog/XUnit</span>
                <span className="tech-pill">Moq</span>
                <p className="tech-title">PHP</p>
                <span className="tech-pill">Laravel</span>
                <span className="tech-pill">Symfony</span>
                <span className="tech-pill">Yii2</span>
                <span className="tech-pill">Bitrix</span>
                <p className="tech-title">Go</p>
                <span className="tech-pill">Revel</span>
                <span className="tech-pill">Beego</span>
                <span className="tech-pill">Gin</span>
                <span className="tech-pill">Buffalo</span>
                <span className="tech-pill">Kit</span>
                <span className="tech-pill">Echo</span>
                <p className="tech-title">Ruby</p>
                <span className="tech-pill">Ruby on Rails</span>
                <span className="tech-pill">Sinatra</span>
                <span className="tech-pill">Hanami (Lotus)</span>
                <p className="tech-title">C++</p>
                <span className="tech-pill">CRYEngine</span>
                <span className="tech-pill">LumberYard</span>
                <span className="tech-pill">UE4</span>
                <span className="tech-pill">Tensorflow</span>
                <span className="tech-pill">Pytorch</span>
                <span className="tech-pill">Geometric tools</span>
                <span className="tech-pill">Cgal</span>
                <span className="tech-pill">MeshLabDDS/MQTT</span>
                <span className="tech-pill">TCP</span>
                <span className="tech-pill">UDP</span>
                <span className="tech-pill">POCO</span>
                <span className="tech-pill">OSP</span>
                <span className="tech-pill">Linux</span>
                <span className="tech-pill">Windows</span>
                <span className="tech-pill">VXWorks</span>
                <p className="tech-title">DB</p>
                <span className="tech-pill">PostgreSQL</span>
                <span className="tech-pill">Oracle</span>
                <span className="tech-pill">MS SQL</span>
                <span className="tech-pill">MongoDB</span>
                <span className="tech-pill">Redis</span>
                <span className="tech-pill">Elasticsearch</span>
              </div>
            </div>
            <div className=" col-md-6" data-aos="fade-up" data-aos-delay="0">
              <div className="row">
                <div
                  className="col-md-12"
                  data-aos="fade-up"
                  data-aos-delay="100"
                >
                  <div className="tech-box">
                    <h5 className="category-title">Frontend</h5>
                    <span className="tech-pill">JavaScript</span>
                    <span className="tech-pill">TypeScript</span>
                    <span className="tech-pill">React</span>
                    <span className="tech-pill">Redux</span>
                    <span className="tech-pill">Vue.js</span>
                    <span className="tech-pill">Angular</span>
                    <span className="tech-pill">Node.js</span>
                    <span className="tech-pill">Next.js</span>
                    <span className="tech-pill">Docker</span>
                  </div>
                </div>
                <div
                  className="col-md-12 mt-5"
                  data-aos="fade-up"
                  data-aos-delay="200"
                >
                  <div className="tech-box">
                    <h5 className="category-title">Mobile</h5>
                    <span className="tech-pill">iOS</span>
                    <span className="tech-pill">Swift</span>
                    <span className="tech-pill">CoreData</span>
                    <span className="tech-pill">RxSwift</span>
                    <span className="tech-pill">Android</span>
                    <span className="tech-pill">Kotlin</span>
                    <span className="tech-pill">MVVM</span>
                    <span className="tech-pill">Retrofit</span>
                    <span className="tech-pill">Flutter</span>
                    <span className="tech-pill">React Native</span>
                  </div>
                </div>
                <div
                  className="col-md-12 mt-5"
                  data-aos="fade-up"
                  data-aos-delay="300"
                >
                  <div className="tech-box">
                    <h5 className="category-title">QA</h5>
                    <span className="tech-pill">Selenium</span>
                    <span className="tech-pill">Postman</span>
                    <span className="tech-pill">Swagger</span>
                    <span className="tech-pill">JMeter</span>
                    <span className="tech-pill">TestRail</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6 " data-aos="fade-up" data-aos-delay="100">
              <div className="tech-box">
                <h5 className="category-title">DevOps</h5>
                <span className="tech-pill">Kubernetes</span>
                <span className="tech-pill">Terraform</span>
                <span className="tech-pill">Ansible</span>
                <span className="tech-pill">Jenkins</span>
                <span className="tech-pill">Docker</span>
              </div>
            </div>
            <div className="col-md-6" data-aos="fade-up" data-aos-delay="200">
              <div className="tech-box">
                <h5 className="category-title">Other</h5>
                <span className="tech-pill">REST</span>
                <span className="tech-pill">SOAP</span>
                <span className="tech-pill">GraphQL</span>
                <span className="tech-pill">JSON</span>
                <span className="tech-pill">CI/CD</span>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      <BlogsMarquee />

      <section className="loction-text">
        <div className="card-container-location container">
          <div className="location-row-7">
            {displayLocations ? (
              displayLocations.map((loc, idx) => (
                <div key={idx} className="col-md-4 col-sm-6 col-6">
                  <div className="card-location">
                    {loc.image?.url && (
                      <Image
                        src={getImageUrl(loc.image.url)}
                        alt={`${loc.country} Office Landmark`}
                        className="location-overlap-img"
                      />
                    )}
                    <h3>{loc.country}</h3>
                    <p>{loc.address}</p>
                    <p>{loc.phone}</p>
                  </div>
                </div>
              ))
            ) : (
              <>
                
                <div className="col-md-4 col-sm-6 col-6">
                  <div className="card-location">
                    <Image
                      src="/assets/img/logo/india.webp"
                      alt="CodeQlik Jaipur HQ Office Landmark"
                      className="location-overlap-img"
                    />
                    <h3>Jaipur</h3>
                    <p>301, 3rd floor 244-245, Dhruv marg, Tilak Nagar, Jaipur</p>
                    <p>+91-8949687368</p>
                  </div>
                </div>
                <div className="col-md-4 col-sm-6 col-6">
                  <div className="card-location">
                    <Image
                      src="/assets/img/logo/indore.webp"
                      alt="CodeQlik Indore Office Landmark"
                      className="location-overlap-img"
                    />
                    <h3>Indore</h3>
                    <p>01, 1st floor Malhar Megha mall, Indore</p>
                    <p>+91-9826096345</p>
                  </div>
                </div>
                <div className="col-md-4 col-sm-6 col-6">
                  <div className="card-location">
                    <Image
                      src="/assets/img/logo/saudi_landmark.png"
                      alt="CodeQlik Saudi Arabia Office Landmark"
                      className="location-overlap-img"
                    />
                    <h3>Saudi Arabia</h3>
                    <p>Umar Ibn Abdul Aziz Br Rd, 21h, Riyadh Saudi Arabia</p>
                    <p>+966-543528976</p>
                  </div>

                </div>
                <div className="col-md-4 col-sm-6 col-6">
                  <div className="card-location">
                    <Image
                      src="/assets/img/logo/dubai.webp"
                      alt="CodeQlik Dubai Office Landmark"
                      className="location-overlap-img"
                    />
                    <h3>Dubai</h3>
                    <p>Building 02 8th floor, Business Bay, Dubai, UAE</p>
                    <p>+971-43799055</p>
                  </div>
                </div>
                <div className="col-md-4 col-sm-6 col-6">
                  <div className="card-location">
                    <Image
                      src="/assets/img/logo/uk.webp"
                      alt="CodeQlik UK Office Landmark"
                      className="location-overlap-img"
                    />
                    <h3>UK</h3>
                    <p>10 Carey St, Reading, Berkshire, RG1 7JS, UK</p>
                    <p>+44-7966 002800</p>
                  </div>
                </div>
                <div className="col-md-4 col-sm-6 col-6">
                  <div className="card-location">
                    <Image
                      src="/assets/img/logo/canada.webp"
                      alt="CodeQlik Canada Office Landmark"
                      className="location-overlap-img"
                    />
                    <h3>Canada</h3>
                    <p>7390 Inverness Street, Vancouver, BC V5X 4H1</p>
                    <p>+1-6049062982</p>
                  </div>
                </div>
                <div className="col-md-4 col-sm-6 col-6">
                  <div className="card-location">
                    <Image
                      src="/assets/img/logo/oman.webp"
                      alt="CodeQlik Oman Office Landmark"
                      className="location-overlap-img"
                    />
                    <h3>Oman</h3>
                    <p>504, 5th Floor, Redtag Building, Alkhuwair, Muscat</p>
                    <p>+968 9696 3180</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
